import { useMemo } from 'react';
import type { PersonalisationOption, PersonalisationValues } from '@/types/catalogue';
import { Input } from '@/components/ui/Input';

/**
 * Renders the personalisation fields a product offers — BUILD-PLAN §10, §15.
 *
 * §10: "The system must dynamically show only the personalization options
 * configured for that product." Nothing here is hard-coded — the field list,
 * their types, limits and choices all come from the API.
 *
 * The constraints shown (max length, allowed choices, date bounds) mirror the
 * server's, so the customer is guided rather than rejected after submitting.
 * They are guidance only: the server revalidates every field against its own
 * config, so bypassing this changes nothing about what is accepted.
 */

interface PersonalisationFormProps {
  options: PersonalisationOption[];
  values: PersonalisationValues;
  onChange: (key: string, value: string) => void;
  /** Field-keyed errors returned by the API, without the `personalisation.` prefix. */
  errors?: Record<string, string>;
}

export function PersonalisationForm({
  options,
  values,
  onChange,
  errors = {},
}: PersonalisationFormProps) {
  if (options.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {options.map((option) => (
        <Field
          key={option.key}
          option={option}
          value={values[option.key] ?? ''}
          error={errors[option.key]}
          onChange={(value) => onChange(option.key, value)}
        />
      ))}
    </div>
  );
}

function Field({
  option,
  value,
  error,
  onChange,
}: {
  option: PersonalisationOption;
  value: string;
  error?: string | undefined;
  onChange: (value: string) => void;
}) {
  const { config } = option;

  // Surcharges are shown inline so the cost of a choice is visible at the
  // point of choosing, not discovered at checkout (§86 no surprises).
  const label = useMemo(
    () => (option.surcharge ? `${option.label} (+${option.surcharge.formatted})` : option.label),
    [option.label, option.surcharge],
  );

  switch (option.type) {
    case 'textarea':
      return (
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`p-${option.key}`}
            className="text-xs font-medium tracking-wide text-ink"
          >
            {label}
            {option.required && <span className="ml-1 text-danger">*</span>}
          </label>

          <textarea
            id={`p-${option.key}`}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            maxLength={config.maxLength ?? 250}
            rows={3}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `p-${option.key}-error` : undefined}
            className={`rounded-sm border bg-surface px-3.5 py-2.5 text-sm text-ink ${
              error ? 'border-danger' : 'border-line focus:border-accent'
            }`}
          />

          <FieldFooter
            option={option}
            error={error}
            // A live counter matters most here: a message silently truncated
            // at the limit is the classic personalisation complaint.
            counter={`${value.length}/${config.maxLength ?? 250}`}
          />
        </div>
      );

    case 'select':
    case 'colour':
      return (
        <fieldset className="flex flex-col gap-2.5">
          <legend className="mb-1 text-xs font-medium tracking-wide text-ink">
            {label}
            {option.required && <span className="ml-1 text-danger">*</span>}
          </legend>

          <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label={option.label}>
            {(config.choices ?? []).map((choice) => {
              const selected = choice.value === value;

              return (
                <button
                  key={choice.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onChange(choice.value)}
                  className={`flex min-h-11 items-center gap-2 rounded-sm border px-4 text-sm transition-colors ${
                    selected
                      ? 'border-accent text-accent'
                      : 'border-line text-ink hover:border-ink'
                  }`}
                >
                  {/* The swatch is decorative; the name carries the meaning,
                      so the control never depends on colour alone (§40). */}
                  {choice.hex && (
                    <span
                      aria-hidden="true"
                      className="h-4 w-4 rounded-full ring-1 ring-line"
                      style={{ backgroundColor: choice.hex }}
                    />
                  )}
                  {choice.label}
                </button>
              );
            })}
          </div>

          <FieldFooter option={option} error={error} />
        </fieldset>
      );

    case 'date':
      return (
        <div className="flex flex-col gap-1.5">
          <Input
            label={label}
            type="date"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            required={option.required}
            error={error}
            {...(config.minDate ? { min: config.minDate } : {})}
            {...(config.maxDate ? { max: config.maxDate } : {})}
          />
          {option.helpText && !error && <Hint>{option.helpText}</Hint>}
        </div>
      );

    // text and monogram share the control; the limits differ.
    default:
      return (
        <div className="flex flex-col gap-1.5">
          <Input
            label={label}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            required={option.required}
            error={error}
            maxLength={config.maxLength ?? (option.type === 'monogram' ? 3 : 40)}
            {...(config.placeholder ? { placeholder: config.placeholder } : {})}
            {...(option.type === 'monogram' ? { autoCapitalize: 'characters' } : {})}
          />
          {option.helpText && !error && <Hint>{option.helpText}</Hint>}
        </div>
      );
  }
}

function FieldFooter({
  option,
  error,
  counter,
}: {
  option: PersonalisationOption;
  error?: string | undefined;
  counter?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      {error ? (
        <p id={`p-${option.key}-error`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : (
        <Hint>{option.helpText}</Hint>
      )}

      {counter && <span className="shrink-0 text-xs text-muted">{counter}</span>}
    </div>
  );
}

function Hint({ children }: { children?: string | null }) {
  if (!children) return <span />;

  return <p className="text-xs text-muted">{children}</p>;
}
